import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { type Metadata } from '@@api/services/metadata/schemas';
import { type ContentEntity } from '@@components/Page/types';

const CONTENT_RESOURCES_STORE = 'contentResourcesStore';

type ContentState = {
    metadata: Metadata | null;
    content: ContentEntity | null;
};

type ContentActions = {
    setContent: (content: ContentEntity) => void;
    setMetadata: (metadata: Metadata) => void;
    clearContentResources: () => void;
};

const initialState: ContentState = {
    content: null,
    metadata: null,
};

export const useContentResourcesStore = create<ContentState & ContentActions>()(
    devtools(
        (set) => ({
            ...initialState,
            setContent: (content) => {
                set({ content }, false, 'setContent');
            },
            setMetadata: (metadata) =>
                set(
                    {
                        metadata,
                    },
                    false,
                    'setMetadata',
                ),
            clearContentResources: () => set({ ...initialState }, false, 'clearContentResources'),
        }),
        { name: CONTENT_RESOURCES_STORE, enabled: true },
    ),
);

export const useContentResourcesLoaded = () => {
    const isLoaded = useContentResourcesStore(
        (state: ContentState) => state.metadata !== null && state.content !== null,
    );
    return isLoaded;
};

/**
 * Selector for safely accessing content once it's guaranteed to be loaded.
 *
 * ⚠️ WARNING: Throws if content is not yet available.
 * Use only in components rendered after the top-level loader confirms data is ready.
 *
 * Helps eliminate unnecessary `null` guards in UI code.
 */
export const useLoadedMetadata = (): Metadata => {
    const metadata = useContentResourcesStore((s) => s.metadata);
    if (!metadata) {
        throw new Error('Metadata used before loaded');
    }
    return metadata;
};

/**
 * Selector for safely accessing metadata once it's guaranteed to be loaded.
 *
 * ⚠️ WARNING: Throws if metadata is not yet available.
 * Use only in components rendered after the top-level loader confirms data is ready.
 *
 * Helps eliminate unnecessary `null` guards in UI code.
 */
export const useLoadedContent = <T extends ContentEntity>(): T => {
    const content = useContentResourcesStore((s) => s.content) as T | null;
    if (!content) {
        throw new Error('Content used before loaded');
    }
    return content;
};
